import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NewlyEarnedBadge } from '../../services/api';
import { BadgeChip } from './BadgeChip';

interface BadgeCelebrationProps {
  badges: NewlyEarnedBadge[];
  onClose: () => void;
  username: string;
}

export function BadgeCelebration({ badges, onClose, username }: BadgeCelebrationProps) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the close button on mount
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  // Escape key to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap within dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, []);

  if (badges.length === 0) {
    return null;
  }

  const handleViewBadges = () => {
    navigate(`/scorecards/user/${username}/badges`);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-ink/85 backdrop-blur flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-celebration-title"
    >
      <div
        ref={dialogRef}
        className="bg-cream rounded-lg max-w-md w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 text-center border-b border-sand">
          <div className="text-[32px] mb-2" aria-hidden="true">
            🏆
          </div>
          <h2 id="badge-celebration-title" className="font-display text-[22px] font-black text-ink">
            {badges.length === 1 ? 'Badge Earned!' : 'Badges Earned!'}
          </h2>
        </div>

        {/* Badge list */}
        <div className="px-6 py-5">
          {badges.map((badge, index) => (
            <div key={badge.badge_key}>
              {index > 0 && <div className="border-b border-sand my-4" />}
              <div className="flex flex-col items-center text-center">
                <BadgeChip
                  badgeKey={badge.badge_key}
                  name={badge.name}
                  description={badge.description}
                  level={badge.level as 1 | 2 | 3 | 4 | 5}
                  emoji={badge.emoji ?? undefined}
                  svgKey={badge.svg_key ?? undefined}
                  earnedAt={badge.earned_at}
                  size="lg"
                  showTooltip={false}
                />
                <p className="font-serif text-[13px] text-warm-gray mt-3 leading-[1.5] max-w-[280px]">
                  {badge.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-5 flex gap-2">
          <button
            onClick={handleViewBadges}
            className="flex-1 bg-gold text-ink font-sans text-[13px] font-bold py-3 rounded hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
          >
            View My Badges
          </button>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="flex-1 bg-ink text-linen font-sans text-[13px] font-semibold py-3 rounded hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
